import React from 'react';
import { ArrowRight, ArrowUpRight } from 'lucide-react';

const loopSteps = ['Collect', 'Surface', 'Investigate', 'Improve'];

const About = () => {
  React.useEffect(() => {
    document.title = 'About Us | Momentedge';
    document.querySelector('meta[name="description"]')?.setAttribute('content', 'Robotics data infrastructure for deployed systems. Momentedge supports both sides of the deployed-robot data workflow: Collect, Surface, Investigate, Improve.');
  }, []);

  return (
    <div className="min-h-screen pt-20" data-testid="about-page">
      {/* Continuous dark background */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950"></div>
        <div className="absolute inset-0 opacity-[0.06]" style={{
          backgroundImage: `
            linear-gradient(45deg, rgb(100, 116, 139) 1px, transparent 1px),
            linear-gradient(-45deg, rgb(100, 116, 139) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px'
        }}></div>
      </div>

      {/* Hero */}
      <section className="relative pt-12 pb-8 md:pt-16 md:pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-[900px] mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-3 leading-tight" data-testid="about-heading">
              Robotics data infrastructure for deployed systems
            </h1>
            <p className="text-lg text-slate-400 leading-[1.7]" data-testid="about-intro">
              Momentedge helps robotics teams collect high-value data, surface important events, and assemble the context needed to investigate what happened.
            </p>
          </div>
        </div>
      </section>

      {/* Why this matters */}
      <section className="relative py-8 md:py-10" data-testid="about-why">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-[900px] mx-auto">
            <h2 className="text-[1.75rem] font-semibold text-white mb-5 pb-2 border-b-2 border-white/10">
              Why this matters
            </h2>
            <div className="space-y-4 text-base text-slate-300 leading-[1.7]">
              <p>
                Real-world deployments produce failures, edge cases, unexpected behavior, and performance issues that are difficult to capture and interpret across large volumes of multimodal data.
              </p>
              <p>
                Known events can be captured efficiently with triggers. Other issues only become visible after deployment, when teams need to search across telemetry, logs, video, sensor data, and task context to understand what happened.
              </p>
              <p>Momentedge is designed to support both sides of that workflow:</p>
            </div>

            <div className="my-6 flex flex-wrap items-center gap-2 md:gap-3" data-testid="data-loop">
              {loopSteps.map((step, i) => (
                <React.Fragment key={step}>
                  <span className="px-4 py-2 rounded-full bg-slate-800/60 border border-slate-700/60 text-white font-semibold text-sm md:text-base backdrop-blur-sm">
                    {step}
                  </span>
                  {i < loopSteps.length - 1 && <ArrowRight className="w-5 h-5 text-slate-500" />}
                </React.Fragment>
              ))}
            </div>

            <p className="text-base text-slate-300 leading-[1.7]">
              What teams learn from deployed robots can inform future collection, incident response, evaluation, simulation, and training.
            </p>
          </div>
        </div>
      </section>

      {/* Working with deployed robot data */}
      <section className="relative py-8 md:py-10" data-testid="about-working">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-[900px] mx-auto">
            <h2 className="text-[1.75rem] font-semibold text-white mb-5 pb-2 border-b-2 border-white/10">
              Working with deployed robot data
            </h2>
            <div className="space-y-4 text-base text-slate-300 leading-[1.7]">
              <p>
                Momentedge analyzes multimodal fleet data to identify events worth attention and assemble the surrounding evidence for investigation.
              </p>
              <p>
                The same workflow can support incident discovery, root-cause analysis, fleet performance analysis, and extraction of useful examples for evaluation or training.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Momentedge Clipper */}
      <section className="relative pt-8 pb-20 md:pt-10 md:pb-24" data-testid="about-clipper">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-[900px] mx-auto bg-slate-800/40 backdrop-blur-sm rounded-2xl p-6 md:p-10 border border-slate-700/50 shadow-lg">
            <h2 className="text-[1.75rem] font-semibold text-white mb-5 pb-2 border-b-2 border-white/10">
              Momentedge Clipper
            </h2>
            <div className="space-y-4 text-base text-slate-300 leading-[1.7]">
              <p>
                Momentedge Clipper is our open-source ROS 2/Rust component for event-triggered data collection.
              </p>
              <p>
                Clipper preserves the context immediately before and after a trigger in MCAP, helping teams retain targeted evidence when they already know what condition matters.
              </p>
              <p>
                It represents the known-event side of the workflow, while broader fleet analysis addresses events that were not predefined.
              </p>
            </div>
            <a
              href="https://github.com/Momentedge/momentedge-clipper"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-clipper-github-link"
              className="mt-8 inline-flex items-center px-6 py-3 bg-white text-slate-900 rounded-lg font-semibold hover:bg-slate-100 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 group"
            >
              Explore Momentedge Clipper on GitHub
              <ArrowUpRight className="ml-2 w-5 h-5 transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
